import {
  emptyObject,
  emptyArray,
  concat,
  identity,
  constant,
} from '@packages/utilities/fp/utils';
import {dissoc, union} from '@packages/utilities/key-map';
import {append} from '@packages/utilities/array';
import {weakMemo} from '@packages/utilities/memo';
import {
  type Checker,
  type CheckerState,
  type Substitution,
  type CheckerEnvironment,
  type Errors,
  type Error,
  getEnvironment,
  getNextUnknown,
  getNextRigid,
  getSubstitution,
  getErrors,
  UndefinedValue,
  UndefinedComponent,
  UndefinedType,
  NonCustomLiteral,
  NonUniqueDistinct,
  MalformedLiteral,
  InfiniteType,
  RigidOutsideScope,
  TypesDoNotUnify,
  MissingField,
  ExtraField,
} from './CheckerState';
import {replaceTypeRefs, retainAnnotations} from './TypeCheckUtils';
import {matchExpression} from '../matchExpression';
import {type ExpressionPathPart} from '../ExpressionPath';
import * as Result from '../Result';
import * as VariableMap from '../VariableMap';
import * as Variable from '../Variable';
import * as EnvTypes from '../EnvTypes';
import {matchType} from '../matchType';
import {
  type ContextualExpression,
  type ContextualExpressionExpr,
  ECall,
  EElement,
  ERecord,
  ESelect,
  EEnum,
  EUnion,
  ECase,
  EList,
  EFunction,
  ETyped,
  EExtend,
  EDistinct,
  annotate,
  stripAnn,
  getAnn,
  updateAnn,
} from '@packages/utilities/contextual-expression';
import {
  type Type,
  type DeconstructedRow,
  type UnknownId,
  type RigidId,
  TGeneric,
} from '../DataTypeDefinition';
import * as Types from '../Types';
import * as TypeImplementation from '../TypeImplementation';
import {
  type Identifier,
  namesEqual,
  toHumanReadableString,
  Qualified,
  type TypeName,
} from '@packages/systems/wfdl-Qualified';
import {
  constructRow,
  tyFunction,
  functionOf,
  functionTypeName,
  booleanTypeName,
  TBoolean,
  numberTypeName,
  TNumber,
  textTypeName,
  TText,
  recordTypeName,
  TRecord,
  unionTypeName,
  TUnion,
  enumTypeName,
  recordOf,
  unionOf,
  enumOf,
  TList,
  ETyped as eETyped,
  type Expression,
  type ExpressionElement,
  TElement,
  TUnknown,
  TRigid,
  TTypeApplication,
  TTypeConstructor,
  TRowEmpty,
  TRowCons,
  constructTypeApplication,
} from '@packages/systems/wfdl-ast';

/**
 * `MutableState` is much like `CheckerState` with 2 differences:
 *
 * 1. We mutate it wherever we can instead of returning new objects;
 *    specifically we mutate this object as well as `substitution` and `errors`.
 *    Only `env` is not being mutated because environments are attached to each
 *    contextual expression
 * 2. We carry the original `CheckerState` (`originalState`) and return it
 *    whenever we didn’t mutate anything in order to aid downstream
 *    memoizations.
 */
type MutableState = {
  env: CheckerEnvironment;
  nextUnknown: UnknownId;
  nextRigid: RigidId;
  substitution: Substitution;
  errors: Errors;
  originalState: CheckerState;
};

/**
 * Compare this to `Checker<a>` which returns a tuple `[CheckerState, a]`.
 * `MutableChecker` returns only `a` since the state is mutated in place.
 */
type MutableChecker<a> = (arg1: MutableState) => a;

const ok = emptyArray;
const notOk = (err: Error) => [err];
const noErrors = emptyArray;

const inElement: ExpressionPathPart = {in: 'Element'};
const inTyped: ExpressionPathPart = {in: 'Typed'};
const inSelect: ExpressionPathPart = {in: 'Select'};
const inFunction: ExpressionPathPart = {in: 'Function'};
const atFun: ExpressionPathPart = {in: 'Call', at: 'fun'};
const atArg: ExpressionPathPart = {in: 'Call', at: 'arg'};
const inExtendAtRecord: ExpressionPathPart = {in: 'Extend', at: 'record'};
const inExtendAtValue: ExpressionPathPart = {in: 'Extend', at: 'value'};
const inDistinct: ExpressionPathPart = {in: 'Distinct'};

const readType = (annotatedExpr: ContextualExpression) =>
  getAnn(annotatedExpr).type;

/**
 * Attaches the provided Type to the annotation of a ContextualExpression
 */
export const setType = (
  expr: ContextualExpression,
  ty: Type
): ContextualExpression =>
  readType(expr) === ty
    ? expr
    : updateAnn(
        (ann) => ({
          type: ty,
          env: ann.env,
          errors: ann.errors,
        }),
        expr
      );

/**
 * Attaches the provided Errors to the annotation of a ContextualExpression
 */
const setErrors = (
  expr: ContextualExpression,
  errors: Errors
): ContextualExpression =>
  updateAnn(
    (ann) => ({
      type: ann.type,
      env: ann.env,
      errors,
    }),
    expr
  );

/**
 * Creates a unique unknown type, recording rigid types found in scope.
 */
const newUnknown: MutableChecker<Type> = (state) => {
  const id = state.nextUnknown;
  const rigids = state.env.context.rigids;
  state.nextUnknown++;

  return TUnknown({id, rigids});
};

/**
 * Creates a unique rigid type.
 */
const newRigid: MutableChecker<RigidId> = (state) => {
  const rigid = state.nextRigid;
  state.nextRigid++;
  return rigid;
};

const withUpdatedEnvironment =
  <a>(
    updateEnv: (arg1: CheckerEnvironment) => CheckerEnvironment,
    action: MutableChecker<a>
  ): MutableChecker<a> =>
  (state) => {
    const env = state.env;
    state.env = updateEnv(env);
    const actionResult = action(state);
    state.env = env;
    return actionResult;
  };

/**
 * Run a Checker computation in an environment where the given
 * ExpressionPathPart is appended to the expression path.
 */
const withPathPart = <a>(
  part: ExpressionPathPart,
  action: MutableChecker<a>
): MutableChecker<a> =>
  withUpdatedEnvironment(
    (env) => ({
      context: env.context,
      expressionPath: append(env.expressionPath, part),
    }),
    action
  );

/**
 * Run a Checker computation in an environment where `rigid` is defined.
 */
const withLocalRigid = <a>(
  rigid: number,
  action: MutableChecker<a>
): MutableChecker<a> =>
  withUpdatedEnvironment(
    (env) => ({
      context: {
        components: env.context.components,
        types: env.context.types,
        values: env.context.values,
        rigids: {...env.context.rigids, [rigid]: true},
      },
      expressionPath: env.expressionPath,
    }),
    action
  );

/**
 * Run a Checker computation in an environment where `identifier` is
 * of type `ty`.
 */
const withLocalVariable = <a>(
  ident: Identifier,
  ty: Type,
  action: MutableChecker<a>
): MutableChecker<a> =>
  withUpdatedEnvironment(
    (env) => ({
      context: {
        components: env.context.components,
        types: env.context.types,
        values: VariableMap.insert(
          env.context.values,
          Variable.unqualified(ident),
          EnvTypes.normalizeEnvType({
            dataType: ty,
            exported: 'visible',
          })
        ),
        rigids: env.context.rigids,
      },
      expressionPath: env.expressionPath,
    }),
    action
  );

/**
 * Check if an unknown occurs inside a type we are trying to unify it with.
 *
 * For example, consider trying to infer the type of the expression
 * `x => [x, x => x]`. Since the types of items in a list need to be the same,
 * this expression is intuitively ill-typed, but let’s see how the inference
 * machinery decides this.
 *
 * - a new unknown, let’s call it `u1`, is created as a placeholder for the
 *   type of the argument `x`
 * - looking at the body, a new unknown, say `u2`, is created as a placeholder
 *   for the type of items in the list
 * - checking the first item in the list, we find that `u2` unifies with `u1`
 * - a type of `u1 => u1` is inferred for the second item, so we we try to unify
 *   `u2` with `u1 => u1`, and since we already know that `u2` is `u1`, what
 *   will be attempted is to unify `u1` with `u1 => u1` which is rejected by
 *   this “occurs” check.
 */
const unknownOccursInType = (
  unknown: {
    id: number;
    rigids: {
      [key: number]: true;
    };
  },
  ty: Type
) => {
  // The same unknown at the top level is fine.
  if (Types.isUnknownType(ty)) false;

  return Types.anyType((t) => {
    const tyUnknown = Types.getUnknownType(t);
    return Boolean(tyUnknown && tyUnknown.id === unknown.id);
  })(ty);
};

/**
 * Inserts a solution into the substitution map if required checks pass.
 */
const solveType =
  (
    unknown: {
      id: number;
      rigids: {
        [key: number]: true;
      };
    },
    ty: Type
  ): MutableChecker<Errors> =>
  (state) => {
    if (unknownOccursInType(unknown, ty)) {
      const err = InfiniteType(ty, state.env.expressionPath);
      state.errors.push(err);
      return notOk(err);
    }

    // Trying to unify an unknown with a rigid that is not in scope.
    // An example occurs when trying to unify the type
    // `<a>(a => a)` with the type `<a>(a => b)`.
    // Since `b` is a type variable bound somewhere else when the
    // return types are unified, it would cause the type of `a` to appear
    // outside of the scope of the function where it is bound, which is non-sensical.
    const rigidId = Types.getRigidType(ty);
    if (typeof rigidId === 'number' && !unknown.rigids[rigidId]) {
      const err = RigidOutsideScope(rigidId, state.env.expressionPath);
      state.errors.push(err);
      return notOk(err);
    }

    state.substitution[unknown.id] = ty;
    return ok;
  };

/**
 * Apply solutions from a substitution map to Unknowns in a type.
 */
export const substituteType = (subst: Substitution) => {
  const go: (ty: Type) => Type = matchType({
    Unknown: ({id}, ty) => {
      const newTy = subst[id];
      const tyUnknown = newTy ? Types.getUnknownType(newTy) : null;
      if (!newTy || (tyUnknown && tyUnknown.id === id)) return ty;
      return go(newTy);
    },
    RowCons: ({label, type, tail}, ty) => {
      const fieldTy = go(type);
      const tailTy = go(tail);
      return type === fieldTy && tail === tailTy
        ? ty
        : retainAnnotations(ty, TRowCons({label, type: fieldTy, tail: tailTy}));
    },
    Generic: ({arg, body}, ty) => {
      const newBody = go(body);
      return body === newBody
        ? ty
        : retainAnnotations(ty, TGeneric({arg, body: newBody}));
    },
    TypeApplication: ({con, arg}, ty) => {
      const newCon = go(con);
      const newArg = go(arg);
      return con === newCon && arg === newArg
        ? ty
        : retainAnnotations(ty, TTypeApplication({con: newCon, arg: newArg}));
    },
    _: identity,
  });

  return go;
};

type AlignedRows = {
  inBoth: {
    [key: string]: {
      left: Type;
      right: Type;
    };
  };
  inLeft: DeconstructedRow;
  inRight: DeconstructedRow;
};

/**
 * Return labels from the first set of fields that don’t exist in the second.
 */
const diffLabels = (
  fields1: {
    [key: string]: Type;
  },
  fields2: {
    [key: string]: Type;
  }
) => Object.keys(fields1).filter((label) => !fields2[label]);

const buildAlignedRowsReducer = (
  acc: {
    result: AlignedRows;
    items1: {
      [key: string]: Type;
    };
    items2: {
      [key: string]: Type;
    };
  },
  label: string
) => {
  const result = acc.result;
  const item1 = acc.items1[label];
  const item2 = acc.items2[label];

  if (item1 && item2) {
    if (result.inBoth === emptyObject) result.inBoth = {};
    result.inBoth[label] = {left: item1, right: item2};
    return acc;
  }

  if (item1) {
    const {inLeft} = result;
    if (inLeft.items === emptyObject) inLeft.items = {};
    inLeft.items[label] = item1;
    return acc;
  }

  // item2 exists here
  const {inRight} = result;
  if (inRight.items === emptyObject) inRight.items = {};
  // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
  inRight.items[label] = item2;
  return acc;
};

const alignRows = weakMemo((row1: Type) =>
  weakMemo((row2: Type): AlignedRows => {
    const {items: items1, tail: tail1} = Types.deconstructRow(row1);
    const {items: items2, tail: tail2} = Types.deconstructRow(row2);

    const acc = {
      result: {
        inBoth: emptyObject,
        inLeft: {items: emptyObject, tail: tail1},
        inRight: {items: emptyObject, tail: tail2},
      },
      items1,
      items2,
    } as const;

    Object.keys(items1).reduce(buildAlignedRowsReducer, acc);
    Object.keys(items2).reduce(buildAlignedRowsReducer, acc);

    return acc.result;
  })
);

const unifyCommonItemsReducer = (
  acc: {
    items: Record<
      string,
      {
        left: Type;
        right: Type;
      }
    >;
    state: MutableState;
    errors: Errors;
  },
  label: string
) => {
  // @ts-expect-error - TS2339 - Property 'left' does not exist on type '{ left: Type; right: Type; } | undefined'. | TS2339 - Property 'right' does not exist on type '{ left: Type; right: Type; } | undefined'.
  const {left, right} = acc.items[label];
  const {state, errors} = acc;
  const itemErrors = unifyTypes(left, right)(state);
  // TODO throw error with the problematic label
  acc.errors = concat(itemErrors)(errors);
  return acc;
};

const unifyRows =
  (row1: Type, row2: Type): MutableChecker<Errors> =>
  (state: MutableState) => {
    const {inBoth, inLeft, inRight} = alignRows(row1)(row2);

    const {errors} = Object.keys(inBoth).reduce(unifyCommonItemsReducer, {
      items: inBoth,
      state,
      errors: emptyArray,
    });

    if (errors.length > 0) return errors;

    const {items: items1, tail: tail1} = inLeft;
    const {items: items2, tail: tail2} = inRight;

    const tyUnknown1 = Types.getUnknownType(tail1);
    if (items1 === emptyObject && tyUnknown1)
      return solveType(tyUnknown1, constructRow(inRight))(state);

    const tyUnknown2 = Types.getUnknownType(tail2);
    if (items2 === emptyObject && tyUnknown2)
      return solveType(tyUnknown2, constructRow(inLeft))(state);

    if (items1 === emptyObject && items2 === emptyObject) {
      if (Types.isRowEmpty(tail1) && Types.isRowEmpty(tail2)) return ok;

      const ref1 = Types.getTypeReference(tail1);
      const ref2 = Types.getTypeReference(tail2);
      if (ref1 && ref2 && ref1 === ref2) return ok;

      const rigidId1 = Types.getRigidType(tail1);
      const rigidId2 = Types.getRigidType(tail2);
      if (
        typeof rigidId1 === 'number' &&
        typeof rigidId2 === 'number' &&
        rigidId1 === rigidId2
      )
        return ok;
    }

    if (tyUnknown1 && tyUnknown2) {
      const rest = newUnknown(state);
      const errors1 = solveType(
        tyUnknown1,
        constructRow({items: items2, tail: rest})
      )(state);
      const errors2 = solveType(
        tyUnknown2,
        constructRow({items: items1, tail: rest})
      )(state);
      return concat(errors2)(errors1);
    }

    const err = TypesDoNotUnify(row1, row2, state.env.expressionPath);
    state.errors.push(err);
    return notOk(err);
  };

/**
 * Checks that the passed in types are the same. During checking we solve found Unknown types based on the current substitution map in state.
 */
const unifyTypes =
  (t1: Type, t2: Type): MutableChecker<Errors> =>
  (state) => {
    const subst = state.substitution;
    // We need to apply the current substitution to both types, otherwise multiple
    // conflicting “solutions” arise for the same unknown type.
    return _unifyTypes(
      substituteType(subst)(t1),
      substituteType(subst)(t2)
    )(state);
  };

const replaceWithAliasImplType = (t: Type, state: MutableState): Type => {
  // If TypeApplication then we have params on an Alias
  const tyApp = Types.deconstructTypeApplication(t);
  const name = tyApp ? tyApp.con : null;
  if (!name) {
    return t;
  }
  const {env} = state;
  const typeImpl = env.context.types.get(name);
  const aliasType = typeImpl ? TypeImplementation.getAliasType(typeImpl) : null;
  if (aliasType === null || typeImpl === undefined) {
    return t;
  }
  // Find potential params from `typeImpl` if we had a `TypeApplication`
  const typeParams = TypeImplementation.getParams(typeImpl);
  // If we have params we need to replace all our references within the alias type with the passed in arguments
  if (!tyApp || !typeParams) {
    return replaceWithAliasImplType(aliasType, state);
  } else {
    const argMap = typeParams.reduce<Record<string, Type>>((acc, curr, idx) => {
      const arg = tyApp.args[idx];
      // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
      acc[curr] = arg;
      return acc;
    }, {});
    const finalType = replaceTypeRefs(argMap, aliasType);
    return replaceWithAliasImplType(finalType, state);
  }
};

// This is otherwise very similar to the Alias counterpart but with Uniques we
// do not want to recurse through the inner types.
const replaceWithUniqueImplType = (
  name: Qualified<TypeName>,
  impl: TypeImplementation.UniqueTypeImplementation,
  state: MutableState
): {
  topLevelType: Type;
  innerType: Type;
} => {
  const innerType = impl.type;

  // Find potential params from `typeImpl` if we had a `TypeApplication`
  const typeParams = TypeImplementation.getParams(impl);
  // If we have params we need to replace all our references within the inner type with the passed in arguments
  if (!typeParams) {
    return {
      topLevelType: TTypeConstructor(name),
      innerType,
    };
  } else {
    const argMap = typeParams.reduce<Record<string, Type>>((acc, curr) => {
      acc[curr] = newUnknown(state);
      return acc;
    }, {});

    // Wraps the unique in as many type applications as there are parameters
    // Each of the parameter is replaced with an unknown
    const topLevelType = constructTypeApplication({
      con: name,
      args: Object.values(argMap),
    });
    return {
      topLevelType,
      innerType: replaceTypeRefs(argMap, innerType),
    };
  }
};

const _unifyTypes =
  (rawT1: Type, rawT2: Type): MutableChecker<Errors> =>
  (state) => {
    const t1 = replaceWithAliasImplType(rawT1, state);
    const t2 = replaceWithAliasImplType(rawT2, state);

    const tyUnknown1 = Types.getUnknownType(t1);
    const tyUnknown2 = Types.getUnknownType(t2);

    if (tyUnknown1 && tyUnknown2 && tyUnknown1.id === tyUnknown2.id) {
      return ok;
    }

    if (tyUnknown1) return solveType(tyUnknown1, rawT2)(state);
    if (tyUnknown2) return solveType(tyUnknown2, rawT1)(state);

    const tyGeneric1 = Types.getGenericType(t1);
    const tyGeneric2 = Types.getGenericType(t2);
    if (tyGeneric1 && tyGeneric2) {
      const rigidId = newRigid(state);
      const rigid = TRigid(rigidId);
      const withRigid1 = replaceTypeRefs(
        {[tyGeneric1.arg]: rigid},
        tyGeneric1.body
      );
      const withRigid2 = replaceTypeRefs(
        {[tyGeneric2.arg]: rigid},
        tyGeneric2.body
      );
      return withLocalRigid(rigidId, unifyTypes(withRigid1, withRigid2))(state);
    }

    if (tyGeneric1) {
      const rigidId = newRigid(state);
      const rigid = TRigid(rigidId);
      const withRigid = replaceTypeRefs(
        {[tyGeneric1.arg]: rigid},
        tyGeneric1.body
      );
      return withLocalRigid(rigidId, unifyTypes(withRigid, t2))(state);
    }

    if (tyGeneric2) {
      const rigidId = newRigid(state);
      const rigid = TRigid(rigidId);
      const withRigid = replaceTypeRefs(
        {[tyGeneric2.arg]: rigid},
        tyGeneric2.body
      );
      return withLocalRigid(rigidId, unifyTypes(withRigid, t1))(state);
    }

    const ref1 = Types.getTypeReference(t1);
    const ref2 = Types.getTypeReference(t2);
    if (ref1 && ref2 && ref1 === ref2) {
      return ok;
    }

    const name1 = Types.getTypeConstructor(t1);
    const name2 = Types.getTypeConstructor(t2);
    if (name1 && name2 && namesEqual(name1, name2)) {
      return ok;
    }

    if (Types.isElementType(t1) && Types.isElementType(t2)) {
      return ok;
    }

    if (
      Types.isRowEmpty(t1) ||
      Types.isRowCons(t1) ||
      Types.isRowEmpty(t2) ||
      Types.isRowCons(t2)
    ) {
      return unifyRows(t1, t2)(state);
    }

    const tyApp1 = Types.getTypeApplication(t1);
    const tyApp2 = Types.getTypeApplication(t2);
    if (tyApp1 && tyApp2) {
      const conErrors = unifyTypes(tyApp1.con, tyApp2.con)(state);
      if (conErrors.length) return conErrors;
      return unifyTypes(tyApp1.arg, tyApp2.arg)(state);
    }

    const rigidId1 = Types.getRigidType(t1);
    const rigidId2 = Types.getRigidType(t2);
    if (
      typeof rigidId1 === 'number' &&
      typeof rigidId2 === 'number' &&
      rigidId1 === rigidId2
    ) {
      return ok;
    }

    const err = TypesDoNotUnify(t1, t2, state.env.expressionPath);
    state.errors.push(err);

    return notOk(err);
  };

/**
 * Get a set of all free type references in a Type
 */
const getFreeTypeRefs: (arg1: Type) => {
  [key: string]: true;
} = weakMemo(
  matchType({
    TypeReference: (ref) => ({[ref]: true}),
    TypeApplication: ({con, arg}) =>
      union(getFreeTypeRefs(con))(getFreeTypeRefs(arg)),
    Generic: ({arg, body}) => {
      const nextRefs: {
        [key: string]: true;
      } = getFreeTypeRefs(body);
      // Remove the argument from nextRefs since it's not a "free" type reference
      if (nextRefs[arg]) {
        return dissoc(arg)(nextRefs);
      }
      return nextRefs;
    },
    RowCons: ({type, tail}) =>
      union(getFreeTypeRefs(type))(getFreeTypeRefs(tail)),
    _: (_) => emptyObject,
  })
);

/**
 *
 * This function removes top-level generics, replacing type variables with
 * fresh unknown types that can be unified with other types. For example, given
 * the expression:
 * ```
 * [(x => x), (x => inc x)]
 * ```
 * The first item is infered to be of type `<a>(a => a)` while the second is
 * presumably infered to be `Number => Number`. Since inside a list all items
 * need to be of the same type, we need to somehow unify the types of the
 * 2 items. What we need in this example is for the type of the first item to
 * not be as general as is inferred, so we remove the generic and replace
 * ocurrances of the type variable with an unknown type variable, yelding a type
 * like `u3 => u3`. During unification, `u3` is determined to be `Number`, and
 * thus everything type-checks.
 */
export const instantiateGenerics = weakMemo((ty: Type) =>
  withMutableState(instantiateTopLevelGenericsWithUnknowns(ty))
);

const instantiateTopLevelGenericsWithUnknowns: (
  arg1: Type
) => MutableChecker<Type> = (ty) => (state: MutableState) => {
  const tyGeneric = Types.getGenericType(ty);
  if (tyGeneric) {
    const {arg, body} = tyGeneric;
    const unknown = newUnknown(state);
    return instantiateTopLevelGenericsWithUnknowns(
      replaceTypeRefs({[arg]: unknown}, body)
    )(state);
  }

  return ty;
};

const checkRowItemSubsumptionReducer = (
  acc: {
    state: MutableState;
    pairs: {
      [key: string]: {
        left: Type;
        right: Type;
      };
    };
    errors: Errors;
  },
  label: string
) => {
  const pair = acc.pairs[label];
  // @ts-expect-error - TS18048 - 'pair' is possibly 'undefined'. | TS18048 - 'pair' is possibly 'undefined'.
  const errs = mutSubsumes(pair.left, pair.right)(acc.state);

  acc.errors = concat(errs)(acc.errors);
  return acc;
};

/**
 * Check that the first type subsumes (is at least as generic as) the second.
 *
 * For example, given a function `f : (Text => Text) => Text` that requires
 * a function `Text => Text` as argument, it is perfectly fine to call it with
 * the identity function (`identity: <a>(a => a)`) which is more generic.
 *
 * Similarly, a function `g: (<a>(List<a> => List<a>)) => Text` can be called
 * with the `identity` function as argument because `identity` is more generic
 * than `g` requires its argument to be. In contrast, a function of type
 * `List<Text> => List<Text>` would not be valid as an argument because it is
 * less generic than required: the function needs to work on lists with
 * any type of items in them.
 *
 * An interesting phenomenon arises when generics are nested within function
 * arguments. Consider the function
 * `h : ((<a>(List<a> => List<a>)) => Text) => Text` which takes as an argument
 * a function which itself takes a generic function as an argument. Is calling
 * `h` with `k: (<a>(a => a) => Text` as an argument well-typed? We’ve seen
 * above that `<a>(a => a)` is more polymorphic than `<a>(List<a> => List<a>)`,
 * however `k` is expecting a function that works for any type it chooses,
 * while `h` promises to provide to `k` a function that only works on lists,
 * or in other words, a less generic function. So the call `h(k)` is ill-typed.
 *
 * The pattern we uncovered here is that a function with a less generic argument
 * is more generic than a function with a more generic argument. This means that
 * whenever encountering functions we need to reverse subsumption check order
 * for their arguments.
 */
export const subsumes = (ty1: Type, ty2: Type): Checker<Errors> =>
  withMutableState(mutSubsumes(ty1, ty2));

const mutSubsumes =
  (initialT1: Type, initialT2: Type): MutableChecker<Errors> =>
  (state) => {
    const t1 = replaceWithAliasImplType(initialT1, state);
    const t2 = replaceWithAliasImplType(initialT2, state);

    const tyGeneric1 = Types.getGenericType(t1);
    if (tyGeneric1) {
      const unknown = newUnknown(state);
      const replaced = replaceTypeRefs(
        {[tyGeneric1.arg]: unknown},
        tyGeneric1.body
      );
      return mutSubsumes(replaced, t2)(state);
    }

    const tyGeneric2 = Types.getGenericType(t2);
    if (tyGeneric2) {
      const rigidId = newRigid(state);
      const rigid = TRigid(rigidId);
      const replaced = replaceTypeRefs(
        {[tyGeneric2.arg]: rigid},
        tyGeneric2.body
      );
      return withLocalRigid(rigidId, mutSubsumes(t1, replaced))(state);
    }

    const t1App = Types.deconstructTypeApplication(t1);
    const t2App = Types.deconstructTypeApplication(t2);

    if (t1App && t2App) {
      const {con: con1, args: args1} = t1App;
      const {con: con2, args: args2} = t2App;

      if (
        args1.length === 2 &&
        args2.length === 2 &&
        namesEqual(con1, functionTypeName) &&
        namesEqual(con2, functionTypeName)
      ) {
        const [arg1, ret1] = args1;
        const [arg2, ret2] = args2;
        // See function description for why args are passed in reverse order here
        // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
        const argRes = mutSubsumes(arg2, arg1)(state);
        if (argRes.length) return argRes;
        // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
        return mutSubsumes(ret1, ret2)(state);
      }

      if (
        args1.length === 1 &&
        args2.length === 1 &&
        ((namesEqual(con1, recordTypeName) &&
          namesEqual(con2, recordTypeName)) ||
          (namesEqual(con1, unionTypeName) &&
            namesEqual(con2, unionTypeName)) ||
          (namesEqual(con1, enumTypeName) && namesEqual(con2, enumTypeName)))
      ) {
        const [row1] = args1;
        const [row2] = args2;
        // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'. | TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
        const {inBoth, inLeft, inRight} = alignRows(row1)(row2);

        const missing = Types.isRowEmpty(inLeft.tail)
          ? diffLabels(inRight.items, inLeft.items).map((label) => {
              const err = MissingField(label, state.env.expressionPath);
              state.errors.push(err);
              return err;
            })
          : emptyArray;

        const extra = Types.isRowEmpty(inRight.tail)
          ? diffLabels(inLeft.items, inRight.items).map((label) => {
              const err = ExtraField(label, state.env.expressionPath);
              state.errors.push(err);
              return err;
            })
          : emptyArray;

        const {errors} = Object.keys(inBoth).reduce(
          checkRowItemSubsumptionReducer,
          {
            state,
            errors: emptyArray,
            pairs: inBoth,
          }
        );

        // If we found missing or extra labels, the unification below will fail
        // so we might as well bail here to not provide redundant errors.
        if (missing.length > 0 || extra.length > 0)
          return concat(errors)(concat(extra)(missing));

        const rowErrors = unifyTypes(
          constructRow(inLeft),
          constructRow(inRight)
        )(state);
        return concat(rowErrors)(errors);
      }
    }

    return unifyTypes(initialT1, initialT2)(state);
  };

const _fail = weakMemo((env: CheckerEnvironment) => {
  return (expr: ContextualExpressionExpr, errors: Errors) =>
    annotate(expr, {type: null, env, errors});
});

/**
 * Type checks a function call, returning it’s type
 */
const checkFunctionCall =
  (
    fun: ContextualExpression,
    arg: Expression,
    expectedRetTy?: Type
  ): MutableChecker<ContextualExpression> =>
  (state) => {
    const funTy = readType(fun);
    const subst = state.substitution;
    return _checkFunctionCall(
      fun,
      funTy ? substituteType(subst)(funTy) : funTy,
      arg,
      expectedRetTy
    )(state);
  };

const _checkFunctionCall =
  (
    fun: ContextualExpression,
    funTy: null | Type,
    arg: Expression,
    expectedRetTy?: Type
  ): MutableChecker<ContextualExpression> =>
  (state) => {
    const tyGeneric = funTy ? Types.getGenericType(funTy) : null;
    if (tyGeneric) {
      const unknownTy = newUnknown(state);
      return _checkFunctionCall(
        fun,
        replaceTypeRefs({[tyGeneric.arg]: unknownTy}, tyGeneric.body),
        arg,
        expectedRetTy
      )(state);
    }

    const {env} = state;
    const fail = _fail(env);

    const typeApp = funTy ? Types.deconstructTypeApplication(funTy) : null;
    if (typeApp) {
      const {con, args} = typeApp;
      if (args.length === 2) {
        const [argTy, retTy] = args;
        const conErrors = unifyTypes(TTypeConstructor(con), tyFunction)(state);

        if (conErrors.length === 0 && retTy && argTy) {
          const errorsBefore = state.errors.length;
          let subsumptionErrors: Errors = [];
          let finalRetTy = retTy;
          let finalArgTy = argTy;
          if (expectedRetTy) {
            // If we provide an expected type, call subsumes to add in potential substitutions to the map
            subsumptionErrors = mutSubsumes(retTy, expectedRetTy)(state);
            const subst = state.substitution;
            finalArgTy = substituteType(subst)(argTy);

            if (subsumptionErrors.length === 0) {
              finalRetTy = expectedRetTy;
            }
          }

          const checkedArg = withPathPart(
            atArg,
            _checkType(arg, finalArgTy)
          )(state);
          const errorsAfter = state.errors.length;
          return annotate(
            ECall({
              fun,
              arg: checkedArg,
            }),
            {
              type: finalRetTy,
              env,
              errors:
                errorsBefore === errorsAfter
                  ? noErrors
                  : state.errors.slice(errorsBefore),
            }
          );
        }
      }
    }

    const inferredArg = withPathPart(atArg, _inferType(arg))(state);
    const inferredArgTy = readType(inferredArg);
    if (!inferredArgTy) return fail(ECall({fun, arg: inferredArg}), noErrors);
    const instantiatedArgTy =
      instantiateTopLevelGenericsWithUnknowns(inferredArgTy)(state);
    const instantiatedArg = setType(inferredArg, instantiatedArgTy);
    if (!funTy) return fail(ECall({fun, arg: instantiatedArg}), noErrors);
    const unknownRetTy = newUnknown(state);
    const errors = unifyTypes(
      funTy,
      functionOf(instantiatedArgTy, unknownRetTy)
    )(state);
    const call = ECall({
      fun,
      arg: instantiatedArg,
    });
    return errors.length === 0
      ? annotate(call, {
          type: unknownRetTy,
          env,
          errors,
        })
      : fail(call, errors);
  };

const checkUnion =
  (tag: string, value: Expression, row: Type) =>
  (state: MutableState): ContextualExpression => {
    const {items, tail} = Types.deconstructRow(row);
    const valueType = items[tag];
    const inUnion = {in: 'Union', tag} as const;

    if (valueType)
      return withPathPart(inUnion, _checkType(value, valueType))(state);

    const {env} = state;
    const fail = _fail(env);
    const inferred = withPathPart(inUnion, _inferType(value))(state);

    if (Types.isRowEmpty(tail)) {
      // TODO: Maybe use new error here
      const err = ExtraField(tag, state.env.expressionPath);
      state.errors.push(err);
      return fail(stripAnn(inferred), notOk(err));
    }

    const inferredTy = readType(inferred);

    if (!inferredTy) return inferred;

    const rest = newUnknown(state);
    unifyTypes(
      tail,
      TRowCons({
        label: tag,
        type: inferredTy,
        tail: rest,
      })
    )(state);

    return inferred;
  };

const checkEnum =
  (tag: string, row: Type) =>
  (state: MutableState): void => {
    const {items, tail} = Types.deconstructRow(row);
    const valueType = items[tag];

    if (valueType) return;

    if (Types.isRowEmpty(tail)) {
      // TODO: Maybe use new error here
      state.errors.push(ExtraField(tag, state.env.expressionPath));
      return;
    }

    const item = newUnknown(state);
    const rest = newUnknown(state);
    unifyTypes(
      tail,
      TRowCons({
        label: tag,
        type: item,
        tail: rest,
      })
    )(state);

    return;
  };

const checkPropReducer = (
  acc: {
    state: MutableState;
    checkedProps: {
      [key: string]: ContextualExpression;
    };
    props: {
      [key: string]: Expression;
    };
    fields: {
      [key: string]: Type;
    };
    tail: null | Type; // only used in checkExtraPropReducer;
  },
  label: string
) => {
  const type = acc.fields[label];
  const prop = acc.props[label];
  if (!prop) {
    acc.state.errors.push(MissingField(label, acc.state.env.expressionPath));
    return acc;
  }
  const checkedField = withPathPart(
    {in: 'Record', at: label},
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    _checkType(prop, type)
  )(acc.state);

  if (acc.checkedProps === emptyObject) acc.checkedProps = {};
  acc.checkedProps[label] = checkedField;
  return acc;
};

const checkExtraPropReducer = (
  acc: {
    state: MutableState;
    checkedProps: {
      [key: string]: ContextualExpression;
    };
    props: {
      [key: string]: Expression;
    };
    fields: {
      [key: string]: Type;
    };
    tail: null | Type;
  },
  label: string
) => {
  if (acc.fields[label]) return acc;
  const {tail} = acc;
  const prop = acc.props[label];

  const inferred = withPathPart(
    {in: 'Record', at: label},
    // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
    _inferType(prop)
  )(acc.state);

  if (acc.checkedProps === emptyObject) acc.checkedProps = {};
  acc.checkedProps[label] = inferred;

  if (!tail || Types.isRowEmpty(tail)) {
    acc.state.errors.push(ExtraField(label, acc.state.env.expressionPath));
    acc.tail = null;
    return acc;
  }

  const inferredTy = readType(inferred);

  const fieldTy = inferredTy ? inferredTy : newUnknown(acc.state);

  const rest = newUnknown(acc.state);
  unifyTypes(
    tail,
    TRowCons({
      label,
      type: fieldTy,
      tail: rest,
    })
  )(acc.state);

  acc.tail = rest;
  return acc;
};

const checkRecord =
  (
    props: {
      [key: string]: Expression;
    },
    row: Type
  ) =>
  (
    state: MutableState
  ): {
    [key: string]: ContextualExpression;
  } => {
    const {items: fields, tail} = Types.deconstructRow(row);

    const fieldsRes = Object.keys(fields).reduce(checkPropReducer, {
      state,
      checkedProps: emptyObject,
      props,
      fields,
      tail,
    });

    const {checkedProps, tail: restTail} = Object.keys(props).reduce(
      checkExtraPropReducer,
      fieldsRes
    );

    if (!restTail) {
      return checkedProps;
    }

    unifyTypes(restTail, TRowEmpty)(state);
    return checkedProps;
  };

const createUnknownsReducer = (
  acc: {
    state: MutableState;
    unknowns: {
      [key: string]: Type;
    };
  },
  ref: string
) => {
  const unknown = newUnknown(acc.state);
  if (acc.unknowns === emptyObject) acc.unknowns = {};
  acc.unknowns[ref] = unknown;
  return acc;
};

const replaceFreeTypeRefsFromTypedData = (
  expr: Expression,
  state: MutableState
): Expression => {
  return matchExpression<Expression>({
    Typed: (typed) => {
      // Get all free type references from typed datatype
      const freeRefs = getFreeTypeRefs(typed.type);
      // Create new unknowns for all free references and populate them into refMapWithUnknowns
      const {unknowns: refMapWithUnknowns} = Object.keys(freeRefs).reduce(
        createUnknownsReducer,
        {state, unknowns: emptyObject}
      );
      // Replace all free type references within component with their respective unknowns
      const instantiatedTypedType = replaceTypeRefs(
        refMapWithUnknowns,
        typed.type
      );

      return eETyped({expr: typed.expr, type: instantiatedTypedType});
    },
    _: (_) => expr,
  })(expr);
};

const checkElement: (
  arg1: ExpressionElement
) => MutableChecker<ContextualExpression> = (el) => (state: MutableState) => {
  const {env} = state;
  const {type: component, data} = el;
  const componentDataType = env.context.components.get(component);
  const fail = _fail(env);

  if (!componentDataType) {
    const err = UndefinedComponent(el.type, state.env.expressionPath);

    const inferredData = withPathPart(inElement, _inferType(data))(state);
    const dataTy = readType(inferredData);
    if (!dataTy) {
      state.errors.push(err);
      return fail(EElement({...el, data: inferredData}), notOk(err));
    }
    const instantiatedDataTy =
      instantiateTopLevelGenericsWithUnknowns(dataTy)(state);
    state.errors.push(err);
    return fail(
      EElement({
        ...el,
        data: setType(inferredData, instantiatedDataTy),
      }),
      notOk(err)
    );
  }

  // Get all free type references from component datatype
  const freeRefs = getFreeTypeRefs(componentDataType);
  // Create new unknowns for all free references and populate them into refMapWithUnknowns
  const {unknowns: refMapWithUnknowns} = Object.keys(freeRefs).reduce(
    createUnknownsReducer,
    {state, unknowns: emptyObject}
  );
  // Replace all free type references within component with their respective unknowns
  const instantiatedComponentDataType = replaceTypeRefs(
    refMapWithUnknowns,
    componentDataType
  );

  const instantiatedData = replaceFreeTypeRefsFromTypedData(data, state);

  const checkedData = withPathPart(
    inElement,
    _checkType(instantiatedData, instantiatedComponentDataType)
  )(state);
  return annotate(
    EElement({
      ...el,
      data: checkedData,
    }),
    {
      type: TElement,
      env,
      errors: noErrors,
    }
  );
};

const inferPropReducer = (
  acc: {
    state: MutableState;
    inferredProps: {
      [key: string]: ContextualExpression;
    };
    fields: null | {
      [key: string]: Type;
    };
    props: {
      [key: string]: Expression;
    };
  },
  key: string
) => {
  const inferredProp = withPathPart(
    {in: 'Record', at: key},
    // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
    _inferType(acc.props[key])
  )(acc.state);

  if (acc.inferredProps === emptyObject) {
    acc.inferredProps = {};
    acc.fields = {};
  }

  acc.inferredProps[key] = inferredProp;

  const {fields} = acc;
  if (fields) {
    const fieldType = readType(inferredProp);
    if (!fieldType) acc.fields = null;
    else fields[key] = fieldType;
  }

  return acc;
};

const inferCasesReducer = (
  acc: {
    state: MutableState;
    inferredCases: {
      [key: string]: ContextualExpression;
    };
    unionRowItems: {
      [key: string]: Type;
    };
    cases: {
      [key: string]: Expression;
    };
    returnType: Type;
    fail: (
      arg1: ContextualExpressionExpr,
      arg2: Errors
    ) => ContextualExpression;
  },
  key: string
) => {
  if (acc.inferredCases === emptyObject) {
    acc.inferredCases = {};
    acc.unionRowItems = {};
  }

  const valTy = newUnknown(acc.state);

  acc.unionRowItems[key] = valTy;

  const inferredCase = withPathPart(
    {in: 'Case', at: {type: 'cases', val: key}},
    // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
    _inferType(acc.cases[key])
  )(acc.state);
  const inferredTy = readType(inferredCase);
  if (!inferredTy) {
    acc.inferredCases[key] = inferredCase;
    return acc;
  }
  const instantiatedTy = instantiateTopLevelGenericsWithUnknowns(inferredTy)(
    acc.state
  );

  const errors = unifyTypes(
    functionOf(valTy, acc.returnType),
    instantiatedTy
  )(acc.state);

  acc.inferredCases[key] =
    errors.length === 0
      ? setType(inferredCase, instantiatedTy)
      : acc.fail(stripAnn(inferredCase), errors);
  return acc;
};

const inferListItemReducer = (
  acc: {
    state: MutableState;
    items: Array<ContextualExpression>;
    itemType: Type;
    fail: (
      arg1: ContextualExpressionExpr,
      arg2: Errors
    ) => ContextualExpression;
  },
  item: Expression,
  index: number
) => {
  const inferredItem = withPathPart(
    {in: 'List', index},
    _inferType(item)
  )(acc.state);
  const inferredItemTy = readType(inferredItem);

  if (acc.items === emptyArray) acc.items = [];

  if (!inferredItemTy) {
    acc.items.push(inferredItem);
    return acc;
  }
  const instantiatedItemTy = instantiateTopLevelGenericsWithUnknowns(
    inferredItemTy
  )(acc.state);
  const errors = unifyTypes(acc.itemType, instantiatedItemTy)(acc.state);

  acc.items.push(
    errors.length === 0
      ? setType(inferredItem, instantiatedItemTy)
      : acc.fail(stripAnn(inferredItem), errors)
  );
  return acc;
};

const toMutableState = (state: CheckerState): MutableState => {
  const subs = getSubstitution(state);
  return {
    env: getEnvironment(state),
    nextUnknown: getNextUnknown(state),
    nextRigid: getNextRigid(state),
    substitution: Object.keys(subs).reduce<Substitution>((clone, key) => {
      // @ts-expect-error string is incompatible with number
      clone[key] = subs[key];
      return clone;
    }, {}),
    errors: getErrors(state).slice(0),
    originalState: state,
  };
};

const fromMutableState = (mutState: MutableState): CheckerState => {
  const {originalState} = mutState;

  if (
    getNextUnknown(originalState) === mutState.nextUnknown &&
    getNextRigid(originalState) === mutState.nextRigid &&
    getErrors(originalState).length === mutState.errors.length &&
    Object.keys(getSubstitution(originalState)).length ===
      Object.keys(mutState.substitution).length
  ) {
    return originalState;
  }

  return {
    env: mutState.env,
    nextUnknown: mutState.nextUnknown,
    nextRigid: mutState.nextRigid,
    substitution: mutState.substitution,
    errors: mutState.errors,
  };
};

const withMutableState = <a>(action: MutableChecker<a>): Checker<a> =>
  weakMemo((state) => {
    const mutState = toMutableState(state);
    const result = action(mutState);
    const finalState = fromMutableState(mutState);
    return [finalState, result];
  });

/**
 * Infer the type of an expression
 */
export const inferType = weakMemo(
  (expr: Expression): Checker<ContextualExpression> =>
    withMutableState(_inferType(expr))
);

const _inferType =
  (expr: Expression): MutableChecker<ContextualExpression> =>
  (state) => {
    const {env} = state;
    const fail = _fail(env);

    return inferTypeHandler(expr)(state, env, fail);
  };

type InferenceMatchExpressionType = (
  arg1: MutableState,
  arg3: CheckerEnvironment,
  arg4: (arg11: ContextualExpressionExpr, arg13: Errors) => ContextualExpression
) => ContextualExpression;
const inferTypeHandler = matchExpression<InferenceMatchExpressionType>({
  Boolean: (_val, expr) => (state, env, _) =>
    annotate(expr, {
      type: TBoolean,
      env,
      errors: noErrors,
    }),
  Number: (_val, expr) => (state, env, _) =>
    annotate(expr, {
      type: TNumber,
      env,
      errors: noErrors,
    }),
  Text: (_val, expr) => (state, env, _) =>
    annotate(expr, {
      type: TText,
      env,
      errors: noErrors,
    }),
  Enum: (val, expr) => (state, env, _1) => {
    const item = newUnknown(state);
    const tail = newUnknown(state);
    return annotate(expr, {
      type: enumOf(
        TRowCons({
          label: val,
          type: item,
          tail,
        })
      ),
      env,
      errors: noErrors,
    });
  },
  Union: (val) => (state, env, fail) => {
    const {tag, value} = val;
    const inferred = withPathPart({in: 'Union', tag}, _inferType(value))(state);
    const newExpr = EUnion({tag, value: inferred});
    const type = readType(inferred);

    if (!type) return fail(newExpr, noErrors);

    // TODO: Consider instantiating value type

    const tail = newUnknown(state);

    return annotate(newExpr, {
      type: unionOf(
        TRowCons({
          label: tag,
          type,
          tail,
        })
      ),
      env,
      errors: noErrors,
    });
  },
  List: (val) => (state, env, fail) => {
    const itemType = newUnknown(state);
    const {items} = val.reduce(inferListItemReducer, {
      state,
      items: emptyArray,
      itemType,
      fail,
    });
    return annotate(EList(items), {
      type: TList(itemType),
      env,
      errors: noErrors,
    });
  },
  Record: (props) => (state, env, fail) => {
    const {inferredProps, fields} = Object.keys(props).reduce(
      inferPropReducer,
      {state, inferredProps: emptyObject, fields: emptyObject, props}
    );

    const rec = ERecord(inferredProps);

    return fields
      ? annotate(rec, {
          type: TRecord(fields),
          env,
          errors: noErrors,
        })
      : fail(rec, noErrors);
  },
  Case: (val) => (state, env, fail) => {
    const {value, cases} = val;
    const inferredValue = withPathPart(
      {in: 'Case', at: {type: 'value'}},
      _inferType(value)
    )(state);
    const returnType = newUnknown(state);

    const {inferredCases, unionRowItems} = Object.keys(cases).reduce(
      inferCasesReducer,
      {
        state,
        inferredCases: emptyObject,
        unionRowItems: emptyObject,
        cases,
        returnType,
        fail,
      }
    );

    const valueTy = readType(inferredValue);

    if (valueTy) {
      const instantiatedValueTy =
        instantiateTopLevelGenericsWithUnknowns(valueTy)(state);

      const errors = unifyTypes(
        instantiatedValueTy,
        TUnion(unionRowItems)
      )(state);
      return errors.length === 0
        ? annotate(
            ECase({
              value: setType(inferredValue, instantiatedValueTy),
              cases: inferredCases,
            }),
            {
              type: returnType,
              env,
              errors,
            }
          )
        : fail(
            ECase({
              value: inferredValue,
              cases: inferredCases,
            }),
            errors
          );
    }

    return fail(
      ECase({
        value: inferredValue,
        cases: inferredCases,
      }),
      noErrors
    );
  },
  Function: (val) => (state, env, fail) => {
    const {param, body} = val;
    const argTy = newUnknown(state);
    const localAction = withPathPart(inFunction, (st) => {
      const inferred = _inferType(body)(st);
      const inferredTy = readType(inferred);
      if (!inferredTy)
        return fail(
          EFunction({
            param,
            body: inferred,
          }),
          noErrors
        );
      const bodyTy = instantiateTopLevelGenericsWithUnknowns(inferredTy)(st);
      return annotate(
        EFunction({
          param,
          body: setType(inferred, bodyTy),
        }),
        {
          type: functionOf(argTy, bodyTy),
          env,
          errors: noErrors,
        }
      );
    });
    return withLocalVariable(param, argTy, localAction)(state);
  },
  Call: (val) => (state, _1, _2) => {
    const {fun, arg} = val;
    const inferredFun = withPathPart(atFun, _inferType(fun))(state);
    return checkFunctionCall(inferredFun, arg)(state);
  },
  Element: (val) => (state, _2, _3) => checkElement(val)(state),
  Variable: (id, expr) => (state, env, fail) => {
    const refTy = VariableMap.get(state.env.context.values, id)?.dataType;
    if (!refTy) {
      const err = UndefinedValue(id, state.env.expressionPath);
      state.errors.push(err);
      return fail(expr, notOk(err));
    }
    return annotate(expr, {
      type: refTy,
      env,
      errors: noErrors,
    });
  },
  Select: (val) => (state, env, fail) => {
    const {from, prop: label} = val;
    const inferredFrom = withPathPart(inSelect, _inferType(from))(state);
    const fromTy = readType(inferredFrom);
    if (!fromTy)
      return fail(
        ESelect({
          from: inferredFrom,
          prop: label,
        }),
        noErrors
      );
    const fieldTy = newUnknown(state);
    const tailTy = newUnknown(state);
    const recordTy = recordOf(
      TRowCons({
        label,
        type: fieldTy,
        tail: tailTy,
      })
    );
    const errors = mutSubsumes(fromTy, recordTy)(state);

    return errors.length === 0
      ? annotate(
          ESelect({
            from: inferredFrom,
            prop: label,
          }),
          {
            type: fieldTy,
            env,
            errors,
          }
        )
      : fail(
          ESelect({
            from: inferredFrom,
            prop: label,
          }),
          noErrors
        );
  },
  Literal: (val, expr) => (state, env, fail) => {
    const {name, value} = val;
    const typeImpl = env.context.types.get(name);

    if (!typeImpl) {
      const err = UndefinedType(name, env.expressionPath);
      state.errors.push(err);

      if (process.env.NODE_ENV !== 'production') {
        console.error(`Missing type ${toHumanReadableString(name)}.`);
      }

      // TODO: Should fail() here but it is causing some crashes in prod.
      return annotate(expr, {
        type: TTypeConstructor(name),
        env,
        errors: notOk(err),
      });
    }

    const handleNonCustomLiteral = <a>(_: a) => {
      const err = NonCustomLiteral(name, env.expressionPath);
      state.errors.push(err);
      return fail(expr, notOk(err));
    };

    return TypeImplementation.match({
      Custom: ({parseValue}) => {
        // Wraps the literal in as many type applications as there are parameters
        // Each of the parameter is replaced with an unknown
        const getType = () => {
          const typeParams = TypeImplementation.getParams(typeImpl);
          const initialValue = TTypeConstructor(name);
          if (!typeParams) {
            return initialValue;
          }
          return typeParams.reduce((acc, _) => {
            return TTypeApplication({
              con: acc,
              arg: newUnknown(state),
            });
          }, initialValue);
        };
        const type = getType();
        if (parseValue) {
          const parseResult = parseValue(value);
          return Result.match<string, unknown, ContextualExpression>({
            Error: (error) => {
              const err = MalformedLiteral(name, error, env.expressionPath);
              state.errors.push(err);

              let stringValue = '';
              try {
                stringValue = JSON.stringify(value);
              } catch (e: any) {
                stringValue = 'NON-JSON';
              }

              if (process.env.NODE_ENV !== 'production') {
                const message = `Literal value ${stringValue} parse failed for ${toHumanReadableString(
                  name
                )}.`;

                console.error(message);
              }

              // TODO: Should fail() here but it is causing some crashes in prod.
              return annotate(expr, {
                type: TTypeConstructor(name),
                env,
                errors: notOk(err),
              });
            },
            Ok: (_) =>
              annotate(expr, {
                type,
                env,
                errors: noErrors,
              }),
          })(parseResult);
        }
        return annotate(expr, {
          type,
          env,
          errors: noErrors,
        });
      },
      Alias: handleNonCustomLiteral,
      Unique: handleNonCustomLiteral,
    })(typeImpl);
  },
  Hole: (_val, expr) => (state, env, _) => {
    const ty = newUnknown(state);
    return annotate(expr, {
      type: ty,
      env,
      errors: noErrors,
    });
  },
  Extend: (val) => (state, env, fail) => {
    const {record, value, prop} = val;

    const inferredRecord = withPathPart(
      inExtendAtRecord,
      _inferType(record)
    )(state);

    const inferredValue = withPathPart(
      inExtendAtValue,
      _inferType(value)
    )(state);

    const recordTy = readType(inferredRecord);
    const valueTy = readType(inferredValue);

    if (!recordTy || !valueTy)
      return fail(
        EExtend({
          record: inferredRecord,
          prop,
          value: inferredValue,
        }),
        noErrors
      );

    const instantiatedRecordTy =
      instantiateTopLevelGenericsWithUnknowns(recordTy)(state);

    const instantiatedValueTy =
      instantiateTopLevelGenericsWithUnknowns(valueTy)(state);

    const tailTy = newUnknown(state);

    const unifyRecordTy = recordOf(
      TRowCons({
        label: prop,
        type: instantiatedValueTy,
        tail: tailTy,
      })
    );

    const errors = unifyTypes(instantiatedRecordTy, unifyRecordTy)(state);

    const ext = EExtend({
      record: setType(inferredRecord, instantiatedRecordTy),
      prop,
      value: setType(inferredValue, instantiatedValueTy),
    });

    return errors.length === 0
      ? annotate(ext, {
          type: unifyRecordTy,
          env,
          errors,
        })
      : fail(ext, errors);
  },
  Typed: (typed) => (state, env, fail) => {
    const contextualExpr = withPathPart(
      inTyped,
      _checkType(typed.expr, typed.type)
    )(state);
    const ty = readType(contextualExpr);

    return ty
      ? annotate(
          ETyped({
            type: typed.type,
            expr: contextualExpr,
          }),
          {
            type: ty,
            env,
            errors: noErrors,
          }
        )
      : fail(ETyped({type: typed.type, expr: contextualExpr}), noErrors);
  },
  Distinct: (val) => (state, env, fail) => {
    const {name, value} = val;

    const typeImpl = env.context.types.get(name);

    if (!typeImpl) {
      const err = UndefinedType(name, env.expressionPath);
      state.errors.push(err);

      if (process.env.NODE_ENV !== 'production') {
        console.error(`Missing type ${toHumanReadableString(name)}.`);
      }

      // Create inferred version of distinct
      const inferredValue = withPathPart(inDistinct, _inferType(value))(state);
      const distinct = EDistinct({name, value: inferredValue});

      return fail(distinct, notOk(err));
    }

    const handleNonUniqueDistinct = <a>(_: a) => {
      const err = NonUniqueDistinct(name, env.expressionPath);
      state.errors.push(err);

      // Create inferred version of distinct
      const inferredValue = withPathPart(inDistinct, _inferType(value))(state);
      const distinct = EDistinct({name, value: inferredValue});

      return fail(distinct, notOk(err));
    };

    return TypeImplementation.match({
      Custom: handleNonUniqueDistinct,
      Alias: handleNonUniqueDistinct,
      Unique: (uniqueImpl) => {
        const {topLevelType, innerType} = replaceWithUniqueImplType(
          name,
          uniqueImpl,
          state
        );

        // Check inner expression values
        const inferredValue = withPathPart(
          inDistinct,
          _checkType(value, innerType)
        )(state);
        const distinct = EDistinct({name, value: inferredValue});

        return annotate(distinct, {
          type: topLevelType,
          env,
          errors: noErrors,
        });
      },
    })(typeImpl);
  },
});

/**
 * Checks that an expression matches the given type in the given context.
 *
 * Type checking and inference go hand in hand. When looking at a subexpression,
 * if we know what the type of it should be we call `checkType`, otherwise we
 * call `inferType`.
 */
export const checkType = (
  expr: Expression,
  originalTy: Type
): Checker<ContextualExpression> => memoCheckState(expr)(originalTy);

const memoCheckState = weakMemo((expr: Expression) =>
  weakMemo((ty: Type) => withMutableState(_checkType(expr, ty)))
);

const _checkType =
  (expr: Expression, originalTy: Type): MutableChecker<ContextualExpression> =>
  (state) => {
    const exprTy = replaceWithAliasImplType(originalTy, state);
    const tyGeneric = Types.getGenericType(exprTy);
    if (tyGeneric) {
      const rigidId = newRigid(state);
      const rigid = TRigid(rigidId);
      const withRigid = replaceTypeRefs(
        {[tyGeneric.arg]: rigid},
        tyGeneric.body
      );
      return withLocalRigid(rigidId, _checkType(expr, withRigid))(state);
    }

    const {env} = state;
    const fail = _fail(env);

    // This case can occur when `checkType` is called by `inferType`
    if (Types.isUnknownType(exprTy)) {
      const inferred = _inferType(expr)(state);
      const inferredTy = readType(inferred);
      if (!inferredTy) return inferred;
      // We don’t support unifying an unknown with a generic type
      const instantiatedTy =
        instantiateTopLevelGenericsWithUnknowns(inferredTy)(state);
      const errors = unifyTypes(instantiatedTy, exprTy)(state);
      return errors.length === 0 ? setType(inferred, originalTy) : inferred;
    }

    return checkTypeHandler(expr)(expr, state, exprTy, env, fail, originalTy);
  };

const defaultTypeCheckReturn = (
  expr: Expression,
  state: MutableState,
  exprTy: Type,
  originalTy: Type
) => {
  const inferred = _inferType(expr)(state);
  const inferredTy = readType(inferred);
  if (!inferredTy) return inferred;
  const errors = mutSubsumes(inferredTy, exprTy)(state);
  return errors.length === 0
    ? setType(inferred, originalTy)
    : setErrors(inferred, errors);
};

const checkListItemReducer = (
  acc: {
    state: MutableState;
    items: Array<ContextualExpression>;
    itemType: Type;
  },
  val: Expression,
  index: number
) => {
  const item = withPathPart(
    {in: 'List', index},
    _checkType(val, acc.itemType)
  )(acc.state);

  if (acc.items === emptyArray) acc.items = [];
  acc.items.push(item);
  return acc;
};

type CheckerMatchExpressionType = (
  arg1: Expression,
  arg2: MutableState,
  arg3: Type,
  arg4: CheckerEnvironment,
  arg5: (
    arg11: ContextualExpressionExpr,
    arg13: Errors
  ) => ContextualExpression,
  arg6: Type
) => ContextualExpression;
const checkTypeHandler = matchExpression<CheckerMatchExpressionType>({
  Boolean: (_val, origExpr) => (expr, state, exprTy, env, fail, originalTy) => {
    const name = Types.getTypeConstructor(exprTy);
    return name && namesEqual(name, booleanTypeName)
      ? annotate(origExpr, {
          type: originalTy,
          env,
          errors: noErrors,
        })
      : defaultTypeCheckReturn(origExpr, state, exprTy, originalTy);
  },
  Number: (_val, origExpr) => (expr, state, exprTy, env, fail, originalTy) => {
    const name = Types.getTypeConstructor(exprTy);
    return name && namesEqual(name, numberTypeName)
      ? annotate(origExpr, {
          type: originalTy,
          env,
          errors: noErrors,
        })
      : defaultTypeCheckReturn(expr, state, exprTy, originalTy);
  },
  Text: (_val, origExpr) => (expr, state, exprTy, env, fail, originalTy) => {
    const name = Types.getTypeConstructor(exprTy);
    return name && namesEqual(name, textTypeName)
      ? annotate(origExpr, {
          type: originalTy,
          env,
          errors: noErrors,
        })
      : defaultTypeCheckReturn(expr, state, exprTy, originalTy);
  },
  List: (list, origExpr) => (expr, state, exprTy, env, fail, originalTy) => {
    const itemType = Types.getListItemType(exprTy);
    if (itemType) {
      const {items} = list.reduce(checkListItemReducer, {
        state,
        items: emptyArray,
        itemType,
      });
      return annotate(EList(items), {
        type: originalTy,
        env,
        errors: noErrors,
      });
    }
    return defaultTypeCheckReturn(origExpr, state, exprTy, originalTy);
  },
  Function: (val, origExpr) => (expr, state, exprTy, env, fail, originalTy) => {
    const tyApp = Types.deconstructTypeApplication(exprTy);
    if (tyApp) {
      const {con, args} = tyApp;
      if (args.length === 2) {
        const [argTy, retTy] = args;
        const conErrors = unifyTypes(TTypeConstructor(con), tyFunction)(state);
        if (conErrors.length === 0) {
          // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
          const action = withPathPart(inFunction, _checkType(val.body, retTy));
          // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
          const body = withLocalVariable(val.param, argTy, action)(state);
          return annotate(
            EFunction({
              param: val.param,
              body,
            }),
            {
              type: originalTy,
              env,
              errors: noErrors,
            }
          );
        }
      }
    }
    return defaultTypeCheckReturn(origExpr, state, exprTy, originalTy);
  },
  Call: (val) => (_0, state, exprTy, _1, fail, originalTy) => {
    const {fun, arg} = val;
    const inferredFun = withPathPart(atFun, _inferType(fun))(state);
    return checkFunctionCall(inferredFun, arg, originalTy)(state);
  },
  Record: (val) => (expr, state, exprTy, env, fail, originalTy) => {
    const tyApp = Types.getTypeApplication(exprTy);
    if (tyApp) {
      const {con, arg} = tyApp;
      const name = Types.getTypeConstructor(con);
      if (name && namesEqual(recordTypeName, name)) {
        const props = checkRecord(val, arg)(state);
        return annotate(ERecord(props), {
          type: originalTy,
          env,
          errors: noErrors,
        });
      }
    }
    return defaultTypeCheckReturn(expr, state, exprTy, originalTy);
  },
  Union:
    (val) =>
    (expr: Expression, state, exprTy: Type, env, fail, originalTy: Type) => {
      const tyApp = Types.getTypeApplication(exprTy);
      if (tyApp) {
        const {con, arg} = tyApp;
        const name = Types.getTypeConstructor(con);
        if (name && namesEqual(unionTypeName, name)) {
          const {tag, value} = val;
          const checkedValue = checkUnion(tag, value, arg)(state);
          const unionExpr = EUnion({tag, value: checkedValue});
          return readType(checkedValue)
            ? annotate(unionExpr, {type: originalTy, env, errors: noErrors})
            : fail(unionExpr, noErrors);
        }
      }
      return defaultTypeCheckReturn(expr, state, exprTy, originalTy);
    },
  Enum:
    (val) =>
    (expr: Expression, state, exprTy: Type, env, fail, originalTy: Type) => {
      const tyApp = Types.getTypeApplication(exprTy);
      if (tyApp) {
        const {con, arg} = tyApp;
        const name = Types.getTypeConstructor(con);
        if (name && namesEqual(enumTypeName, name)) {
          const tag = val;
          checkEnum(tag, arg)(state);
          return annotate(EEnum(tag), {
            type: originalTy,
            env,
            errors: noErrors,
          });
        }
      }
      return defaultTypeCheckReturn(expr, state, exprTy, originalTy);
    },
  _: constant((expr, state, exprTy, _, fail, originalTy) =>
    defaultTypeCheckReturn(expr, state, exprTy, originalTy)
  ),
});
